import { GridColDef } from '@mui/x-data-grid-pro';

import * as analytics from '../../../shared/analytics/analytics.json';
import {
  getOrgIndexLogs,
  indexAllOrgs,
  UserKeys,
} from '../../../api/users-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAnalytics } from '../../../shared/analytics/create-events';
import { useStore } from '../../state/zustand-store';
import { handleErrors } from '../../../utils/errors';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { useEffect, useMemo, useState } from 'react';
import { useUserSettings } from '../../../utils/use-user-settings';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../shared/shared-toolbar-latest';
import ChangeLogDialog from '../../../shared/change-log-dialog';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import SharedTabHeader from '../../../shared/shared-tab-header';
import Apartment from '@mui/icons-material/Apartment';
import Refresh from '@mui/icons-material/Refresh';

export default function OrgsIndexingGrid() {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const { setSnackbar, orgIndexingIsDisabled } = useStore();
  const [fakeLoading, setFakeLoading] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);

  const { administrationRoles, changeLogRoles } = useUserSettings();

  useEffect(() => {
    if (fakeLoading) {
      setTimeout(() => {
        setFakeLoading(false);
      }, 3000);
    }
  }, [fakeLoading]);

  useEffect(() => {
    if (!orgIndexingIsDisabled) {
      setFakeLoading(false);
    }
  }, [orgIndexingIsDisabled]);

  const {
    data: indexOrgLogs,
    isLoading: isIndexOrgLogsLoading,
    isFetching: isIndexOrgLogsFetching,
  } = useQuery({
    queryKey: [UserKeys.IndexOrgLog],
    queryFn: () => getOrgIndexLogs(),
    meta: {
      errorMessage:
        'There was an issue loading Org Units Index Log. Please refresh and try again.',
    },
  });

  const mostRecentRunTime = indexOrgLogs?.reduce((latest, log) => {
    const logTime = new Date(log.lastRunTime);
    return logTime > latest ? logTime : latest;
  }, new Date(0));

  const { mutate, isPending } = useMutation({
    mutationFn: () => indexAllOrgs(changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'Indexing for org units is now running.',
      });
      setOpenChangeLogDialog(false);
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue indexing the org units. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.administration.indexing.index_all_orgs, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.indexing,
      });
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        AdministrationRoles.RunOrgIndexing.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
      setOpenChangeLogDialog(false);
      setChangeLogMessage('');
    }
  }

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'lastRunTime',
        headerName: 'Run Time',
        width: 180,
        type: 'dateTime',
        valueGetter: (params: number) => {
          return new Date(params);
        },
        valueFormatter: (params: Date) => {
          return params.toLocaleString();
        },
      },
      {
        field: 'manual',
        headerName: 'Manual Run',
        type: 'boolean',
        flex: 1,
      },
      {
        field: 'emailDomain',
        headerName: 'Email Domain',
        flex: 1,
      },
    ],
    []
  );

  function CustomToolbar() {
    return (
      <SharedToolbar noColumn noFilter noQuickFilter>
        <Stack
          flexDirection="row"
          justifyContent="flex-end"
          alignItems="center"
          gap={2}
        >
          Last Run Time:{' '}
          {mostRecentRunTime
            ? mostRecentRunTime.toLocaleString()
            : 'No runs yet'}
          <Tooltip arrow title="Refresh">
            <IconButton
              onClick={() => {
                queryClient.invalidateQueries({
                  queryKey: [UserKeys.IndexOrgLog],
                });
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </Stack>
      </SharedToolbar>
    );
  }

  const disabledByRole = !isAbleToSeeUIElement(
    administrationRoles,
    AdministrationRoles.RunOrgIndexing.value
  );

  return (
    <Stack gap={2}>
      <SharedTabHeader title="Organizational Units" />
      <Box sx={{ px: 1 }}>
        <Stack gap={2} mb={2}>
          <Stack flexDirection="row" alignItems="center">
            <Tooltip
              title={
                disabledByRole
                  ? `You need the permission "${AdministrationRoles.RunOrgIndexing.name}" to perform this action.`
                  : ''
              }
            >
              <span>
                <Button
                  variant="contained"
                  loading={fakeLoading || orgIndexingIsDisabled}
                  disabled={
                    disabledByRole || fakeLoading || orgIndexingIsDisabled
                  }
                  onClick={handleSubmit}
                >
                  Index All
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
        <Box sx={{ height: '50vh', mx: 'auto' }}>
          <SharedDataGridClient
            columns={columns}
            rows={indexOrgLogs || []}
            slots={{
              toolbar: CustomToolbar,
              ...createGridOverlays(
                'Organizational Unit Indexing Logs',
                <Apartment />
              ),
            }}
            getRowId={(row) => row.lastRunTime}
            loading={isIndexOrgLogsLoading || isIndexOrgLogsFetching}
          />
        </Box>
      </Box>
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          mutate();
        }}
        mutateLoading={isPending}
      />
    </Stack>
  );
}
